import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;

    /* Maintenance window opened while the user was signed in — drop them on
       the notice screen instead of letting every panel fail on its own. */
    if (err.response?.status === 503 && err.response.data?.maintenance) {
      if (!window.location.pathname.startsWith("/maintenance")) {
        window.location.href = "/maintenance";
      }
      return Promise.reject(err);
    }

    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        try {
          const { data } = await axios.post(
            `${API_BASE}/api/wallet/auth/refresh-token`,
            { refreshToken },
          );
          localStorage.setItem("accessToken", data.accessToken);
          localStorage.setItem("refreshToken", data.refreshToken);
          original.headers.Authorization = `Bearer ${data.accessToken}`;
          return api(original);
        } catch {
          localStorage.clear();
          window.location.href = "/signin";
        }
      }
    }
    return Promise.reject(err);
  },
);

/* Auth */
export const requestOtp = (identifier, channel) =>
  api.post("/api/wallet/auth/request-otp", { identifier, channel });
export const verifyOtp = (identifier, otp) =>
  api.post("/api/wallet/auth/verify-otp", { identifier, otp });
export const verifyMpin = (mpin) =>
  api.post("/api/wallet/auth/verify-mpin", { mpin });
export const loginStart = (identifier) =>
  api.post("/api/wallet/auth/login-start", { identifier });
export const loginWithPassword = (identifier, password, mpin) =>
  api.post("/api/wallet/auth/login-password", { identifier, password, mpin });
export const setCredentials = (password, mpin) =>
  api.post("/api/wallet/auth/set-credentials", { password, mpin });
export const refreshTokenApi = (refreshToken) =>
  api.post("/api/wallet/auth/refresh-token", { refreshToken });
export const logoutApi = () => api.post("/api/wallet/auth/logout");

/* Users */
export const inviteUser = (body) => api.post("/api/wallet/users/invite", body);
export const getInviteDetails = (token) =>
  api.get(`/api/wallet/users/invite/${token}`);
export const registerUser = (body) =>
  api.post("/api/wallet/users/register", body);
export const getProfile = () => api.get("/api/wallet/users/me");
export const updateProfile = (body) => api.put("/api/wallet/users/me", body);
export const changeMpin = (body) => api.put("/api/wallet/users/me/mpin", body);
export const getDownline = (page = 1, limit = 50, roleId) =>
  api.get("/api/wallet/users/downline", { params: { page, limit, roleId } });
export const getInvitesSent = (page = 1, limit = 50) =>
  api.get("/api/wallet/users/invites-sent", { params: { page, limit } });
export const revokeInvite = (id) =>
  api.delete(`/api/wallet/users/invites/${id}`);
export const getDownlineTree = () => api.get("/api/wallet/users/downline/tree");
export const getDownlineUser = (id) => api.get(`/api/wallet/users/${id}`);
export const toggleUserStatus = (id, isActive) =>
  api.put(`/api/wallet/users/${id}/status`, { isActive });
export const updateUserLimits = (id, body) =>
  api.put(`/api/wallet/users/${id}/limits`, body);

/* Wallet */
export const getBalance = () => api.get("/api/wallet/wallet");
export const getTransactions = (params) =>
  api.get("/api/wallet/wallet/transactions", { params });
export const getWalletSummary = () => api.get("/api/wallet/wallet/summary");

/* Commissions */
export const getCommissionConfig = () =>
  api.get("/api/wallet/commissions/config");
export const setCommissionConfig = (rates) =>
  api.put("/api/wallet/commissions/config", { rates });
export const getCommissionEarnings = (page = 1) =>
  api.get("/api/wallet/commissions/earnings", { params: { page, limit: 50 } });

/* Commission withdrawal requests (user side).
   amount omitted / null → request the full commission balance. */
export const raiseCommissionWithdrawal = (amount, note) =>
  api.post("/api/wallet/commissions/withdraw-requests", { amount, note });
export const getMyCommissionWithdrawals = () =>
  api.get("/api/wallet/commissions/withdraw-requests");
export const getAllCommissionOverrides = () =>
  api.get("/api/wallet/commissions/all-overrides");
export const getUserCommissionChain = (userId) =>
  api.get(`/api/wallet/commissions/user-chain/${userId}`);
export const setCommissionOverride = (
  userId,
  beneficiaryUserId,
  percentage,
  gateway = "default",
) =>
  api.put(`/api/wallet/commissions/overrides/${userId}`, {
    beneficiaryUserId,
    percentage,
    gateway,
  });
export const deleteCommissionOverride = (
  userId,
  beneficiaryUserId,
  gateway = "default",
) =>
  api.delete(`/api/wallet/commissions/overrides/${userId}`, {
    params: { beneficiaryUserId, gateway },
  });

/* Gateways */
export const getPayinGateways = () => api.get("/api/wallet/gateways/payin");
export const getPayoutGateways = () => api.get("/api/wallet/gateways/payout");
export const getAdminPayinGateways = () =>
  api.get("/api/wallet/gateways/admin/payin");
export const setAdminPayinGateways = (disabled) =>
  api.put("/api/wallet/gateways/admin/payin", { disabled });
export const getAdminUserPayinGateways = (id) =>
  api.get(`/api/wallet/gateways/admin/user/${id}/payin`);
export const setAdminUserPayinGateways = (id, disabled) =>
  api.put(`/api/wallet/gateways/admin/user/${id}/payin`, { disabled });

/* Payins */
export const initiatePayin = (
  amount,
  paymentFor,
  gateway,
  gatewayId,
  customer,
  cardLast4,
) =>
  api.post("/api/wallet/payins/initiate", {
    amount,
    paymentFor,
    gateway,
    gatewayId,
    customer,
    cardLast4,
  });
export const checkPayinStatus = (payinId) =>
  api.get(`/api/wallet/payins/${payinId}/status`);
export const listPayins = (page = 1) =>
  api.get("/api/wallet/payins", { params: { page, limit: 20 } });

/* Settings — admin-configurable menu visibility per role */
export const getMenuConfig = () => api.get("/api/wallet/settings/menu");
export const setMenuConfig = (menu) =>
  api.put("/api/wallet/settings/menu", { menu });

/* Payout charges (Gmax) */
export const getPayoutChargeQuote = (amount) =>
  api.get("/api/wallet/settings/payout-charges/quote", { params: { amount } });
export const getPayoutChargeConfig = () =>
  api.get("/api/wallet/settings/payout-charges");
export const setPayoutChargeConfig = (config) =>
  api.put("/api/wallet/settings/payout-charges", config);

/* ---- Offline collections: Pinelabs POS + QR ---- */

/* User */
export const getOfflineOptions = () => api.get("/api/wallet/offline/options");
export const submitOfflineCollection = (body) =>
  api.post("/api/wallet/offline/collections", body);
export const listMyOfflineCollections = (page = 1, limit = 20) =>
  api.get("/api/wallet/offline/collections", { params: { page, limit } });

/* Admin — QR codes */
export const adminListQrCodes = () =>
  api.get("/api/wallet/offline/admin/qr-codes");
export const adminCreateQrCode = (body) =>
  api.post("/api/wallet/offline/admin/qr-codes", body);
export const adminUpdateQrCode = (id, body) =>
  api.put(`/api/wallet/offline/admin/qr-codes/${id}`, body);
export const adminDeleteQrCode = (id) =>
  api.delete(`/api/wallet/offline/admin/qr-codes/${id}`);

/* Admin — POS machines */
export const adminListPosMachines = () =>
  api.get("/api/wallet/offline/admin/pos-machines");
export const adminCreatePosMachine = (body) =>
  api.post("/api/wallet/offline/admin/pos-machines", body);
export const adminUpdatePosMachine = (id, body) =>
  api.put(`/api/wallet/offline/admin/pos-machines/${id}`, body);
export const adminDeletePosMachine = (id) =>
  api.delete(`/api/wallet/offline/admin/pos-machines/${id}`);
export const adminGetUserPosMachines = (userId) =>
  api.get(`/api/wallet/offline/admin/users/${userId}/pos-machines`);
export const adminSetUserPosMachines = (userId, machineIds) =>
  api.put(`/api/wallet/offline/admin/users/${userId}/pos-machines`, {
    machineIds,
  });

/* Admin — approval queue */
export const adminListOfflineCollections = (params) =>
  api.get("/api/wallet/offline/admin/collections", { params });
export const adminApproveOfflineCollection = (id, body) =>
  api.post(`/api/wallet/offline/admin/collections/${id}/approve`, body);
export const adminRejectOfflineCollection = (id, reason) =>
  api.post(`/api/wallet/offline/admin/collections/${id}/reject`, { reason });

/* Move a user onto a different upline (their downline travels with them) */
export const adminGetMovePreview = (userId) =>
  api.get(`/api/wallet/admin/users/${userId}/move-preview`);
export const adminMoveUser = (userId, newParentId, reason) =>
  api.post(`/api/wallet/admin/users/${userId}/move`, { newParentId, reason });

/* Login OTP channels (public read — the sign-in page needs it pre-auth) */
export const getOtpChannels = () =>
  api.get("/api/wallet/settings/otp-channels");
export const setOtpChannels = (channels) =>
  api.put("/api/wallet/settings/otp-channels", channels);

/* Maintenance / site timing window */
export const getMaintenanceStatus = () =>
  api.get("/api/wallet/settings/maintenance");
export const getMaintenanceConfig = () =>
  api.get("/api/wallet/settings/maintenance/config");
export const setMaintenanceConfig = (config) =>
  api.put("/api/wallet/settings/maintenance/config", config);

/* Cashfree payin fee % (admin-configurable) */
export const getCashfreeFee = () =>
  api.get("/api/wallet/settings/cashfree-fee");
export const setCashfreeFee = (percent) =>
  api.put("/api/wallet/settings/cashfree-fee", { percent });

/* BBPS */
export const getBbpsCategories = () => api.get("/api/wallet/bbps/categories");
export const getBbpsBillers = (params) =>
  api.get("/api/wallet/bbps/billers", { params });
export const fetchBbpsBill = (billerId, inputParams) =>
  api.post("/api/wallet/bbps/fetch-bill", { billerId, inputParams });
export const payBbpsBill = (body) => api.post("/api/wallet/bbps/pay", body);
export const listBbpsPayments = (page = 1, limit = 20) =>
  api.get("/api/wallet/bbps/payments", { params: { page, limit } });
export const getBbpsPayment = (id) => api.get(`/api/wallet/bbps/payments/${id}`);
export const refreshBbpsPayment = (id) =>
  api.post(`/api/wallet/bbps/payments/${id}/refresh`);

/* BBPS — flat per-role charge */
export const getMyBbpsCharge = () => api.get("/api/wallet/bbps/charges/me");

/* BBPS — admin biller allow-list */
export const getAdminBbpsBillers = (params) =>
  api.get("/api/wallet/bbps/admin/billers", { params });
export const setBbpsBillersEnabled = (billerIds, enabled) =>
  api.put("/api/wallet/bbps/admin/billers/enabled", { billerIds, enabled });

/* BBPS — admin flat per-role charge config */
export const getAdminBbpsCharges = () =>
  api.get("/api/wallet/bbps/admin/charges");
export const setAdminBbpsCharge = (roleLevel, charge) =>
  api.put("/api/wallet/bbps/admin/charges", { roleLevel, charge });

/* SuperPe cards + top-up */
export const getSuperpeCardAesKey = () =>
  api.get("/api/wallet/superpe/cards/aes-key");
export const registerSuperpeCard = (body) =>
  api.post("/api/wallet/superpe/cards", body);
export const listSuperpeCards = () => api.get("/api/wallet/superpe/cards");
export const deleteSuperpeCard = (cardId) =>
  api.delete(`/api/wallet/superpe/cards/${cardId}`);
export const initiateSuperpeTopup = (amount, cardId, cvv) =>
  api.post("/api/wallet/superpe/topup", { amount, cardId, cvv });
export const checkSuperpeTopupStatus = (payinId) =>
  api.get(`/api/wallet/superpe/topup/${payinId}/status`);

/* Payouts */
export const requestPayout = (body) =>
  api.post("/api/wallet/payouts/request", body);
export const listPayouts = (page = 1) =>
  api.get("/api/wallet/payouts", { params: { page, limit: 20 } });
export const approvePayout = (id) =>
  api.put(`/api/wallet/payouts/${id}/approve`);
export const rejectPayout = (id, reason) =>
  api.put(`/api/wallet/payouts/${id}/reject`, { reason });

/* Admin */
export const adminDashboard = () => api.get("/api/wallet/admin/dashboard");
export const adminListUsers = (params) =>
  api.get("/api/wallet/admin/users", { params });
export const adminGetUserDetail = (id) =>
  api.get(`/api/wallet/admin/users/${id}`);
export const adminListPayins = (params) =>
  api.get("/api/wallet/admin/payins", { params });
export const adminListPayouts = (params) =>
  api.get("/api/wallet/admin/payouts", { params });
export const adminListTransactions = (params) =>
  api.get("/api/wallet/admin/transactions", { params });
export const lockUser = (id, reason) =>
  api.post(`/api/wallet/admin/users/${id}/lock`, { reason });
export const unlockUser = (id) =>
  api.post(`/api/wallet/admin/users/${id}/unlock`);
/* Admin — full commission ledger for a user (paginated, verifiable) */
export const adminGetUserCommissions = (id, page = 1, limit = 50) =>
  api.get(`/api/wallet/admin/users/${id}/commissions`, {
    params: { page, limit },
  });

/* Admin — commission withdrawal request queue */
export const adminListCommissionRequests = (params) =>
  api.get("/api/wallet/admin/commission-requests", { params });
export const adminPayCommissionRequest = (id, note) =>
  api.post(`/api/wallet/admin/commission-requests/${id}/pay`, { note });
export const adminRejectCommissionRequest = (id, reason) =>
  api.post(`/api/wallet/admin/commission-requests/${id}/reject`, { reason });

/* Admin — edit user, block login, KYC status */
export const adminUpdateUser = (id, body) =>
  api.put(`/api/wallet/admin/users/${id}`, body);
export const adminSetUserActive = (id, isActive) =>
  api.put(`/api/wallet/admin/users/${id}/active`, { isActive });
export const adminSetApproval = (id, status, reason) =>
  api.put(`/api/wallet/admin/users/${id}/approval`, { status, reason });
export const adminUploadKyc = (id, docType, dataUrl) =>
  api.post(`/api/wallet/admin/users/${id}/kyc`, { docType, dataUrl });

export const adjustUserWallet = (id, body) =>
  api.post(`/api/wallet/admin/users/${id}/wallet-adjust`, body);

/* Admin — pending/unresolved BBPS bills queue */
export const getAdminPendingBbps = () =>
  api.get("/api/wallet/admin/bbps/pending");
export const resolvePendingBbps = (id, note) =>
  api.post(`/api/wallet/admin/bbps/pending/${id}/resolve`, { note });

/* Admin — reports / analytics */
export const getReportSummary = (params) =>
  api.get("/api/wallet/admin/reports/summary", { params });
export const getReportUsers = (params) =>
  api.get("/api/wallet/admin/reports/users", { params });
export const getReportUserTransactions = (id, params) =>
  api.get(`/api/wallet/admin/reports/users/${id}/transactions`, { params });
export const adminGetUserPassbook = (id, params) =>
  api.get(`/api/wallet/admin/users/${id}/passbook`, { params });

/* Finkeda (PG + Payout) — retailer */
export const getFinkedaGateways = () =>
  api.get("/api/wallet/finkeda/gateways");
export const finkedaQuote = (body) =>
  api.post("/api/wallet/finkeda/quote", body);
export const finkedaInitiate = (body) =>
  api.post("/api/wallet/finkeda/initiate", body);
export const listFinkedaTransactions = (params) =>
  api.get("/api/wallet/finkeda/transactions", { params });
export const listFinkedaAdminTransactions = (params) =>
  api.get("/api/wallet/finkeda/admin/transactions", { params });
export const finkedaAdminEarnings = () =>
  api.get("/api/wallet/finkeda/admin/earnings");
export const refreshFinkedaTransaction = (id) =>
  api.post(`/api/wallet/finkeda/transactions/${id}/refresh`);
export const refreshFinkedaByUrn = (urn) =>
  api.post(`/api/wallet/finkeda/transactions/refresh-by-urn`, { urn });
/* Finkeda — admin */
export const finkedaAdminConfig = () =>
  api.get("/api/wallet/finkeda/admin/config");
export const finkedaGatewayTypes = () =>
  api.get("/api/wallet/finkeda/admin/gateway-types");
export const finkedaTokenTest = () =>
  api.get("/api/wallet/finkeda/admin/token-test");
export const finkedaBalance = () =>
  api.get("/api/wallet/finkeda/admin/balance");
export const setFinkedaGateway = (key, body) =>
  api.put(`/api/wallet/finkeda/admin/gateways/${key}`, body);
export const setFinkedaCharge = (body) =>
  api.put("/api/wallet/finkeda/admin/charges", body);
export const setFinkedaSettings = (body) =>
  api.put("/api/wallet/finkeda/admin/settings", body);
export const setFinkedaPayoutSlabs = (body) =>
  api.put("/api/wallet/finkeda/admin/payout-slabs", body);

/* Admin — user approval queue (pending KYC) */
export const getAdminApprovals = () => api.get("/api/wallet/admin/approvals");
export const approveUser = (id) =>
  api.post(`/api/wallet/admin/approvals/${id}/approve`);
export const rejectUser = (id, reason) =>
  api.post(`/api/wallet/admin/approvals/${id}/reject`, { reason });

export default api;
